import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { siteConfig } from '@/mocks/siteData';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [serverMessage, setServerMessage] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormState('success');
      setServerMessage('感谢您的留言，我们会尽快回复您！');
      return;
    }

    setFormState('submitting');
    setFormError('');
    setServerMessage('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9phcu1n2trhb35gvfe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setServerMessage(parsed?.meta?.message || '感谢您的留言，我们会尽快回复您！');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormState('error');
        setFormError(errMsg);
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查您的网络连接后重试');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: siteConfig.brand.intro,
    url: `${siteConfig.url}/about`,
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.brand.name,
    url: siteConfig.url,
    description: siteConfig.brand.intro,
    foundingDate: siteConfig.brand.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.brand.contact.phone,
      email: siteConfig.brand.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.brand.contact.address,
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <PageMeta
        title={`关于我们 - ${siteConfig.name}`}
        description={siteConfig.brand.intro}
        canonical={`${siteConfig.url}/about`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="w-full max-w-[1100px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Hero */}
          <div className="text-center py-10 md:py-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于我们
            </h1>
            <p className="text-foreground-600 text-base max-w-[600px] mx-auto leading-relaxed">
              {siteConfig.brand.intro}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
            {siteConfig.brand.stats.map((stat) => (
              <div
                key={stat.label}
                className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading mb-2">
                  {stat.value}
                </div>
                <div className="text-foreground-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Brand Introduction */}
          <section className="mb-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="text-foreground-700 text-sm leading-relaxed space-y-3">
                  <p>
                    17c入口导航成立于{siteConfig.brand.founded}年，是国内最早专注于提供{siteConfig.name}服务的平台之一。我们的创始团队拥有超过十年的互联网行业经验，深知用户在访问各类平台时面临的困扰与挑战。
                  </p>
                  <p>
                    起初，我们只是一个小型的入口信息分享社区。随着越来越多的用户通过我们成功解决了访问难题，我们意识到这项服务的巨大价值。于是，我们投入了更多的资源来完善入口监控系统和信息更新机制。
                  </p>
                  <p>
                    如今，17c入口导航已发展成为一个拥有专业团队、完善技术设施和庞大用户群体的综合性入口导航平台。我们始终坚持以用户需求为导向，不断优化服务体验。
                  </p>
                </div>
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                  平台信誉
                </h2>
                <div className="text-foreground-700 text-sm leading-relaxed space-y-3">
                  <p>
                    {siteConfig.brand.reputation}
                  </p>
                  <p>
                    我们的专业团队7×24小时监控入口状态，确保用户随时都能通过正确的入口地址顺利访问17c com平台。任何一个入口出现异常，我们都会在第一时间通知用户并提供替代方案。
                  </p>
                  <div className="flex items-center gap-2 mt-4 p-4 rounded-lg bg-accent-50 border border-accent-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                    <p className="text-accent-800 text-sm">
                      所有入口地址均经过安全审核，请放心使用
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-14">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-5">
                    联系方式
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                        <a href={`mailto:${siteConfig.brand.contact.email}`} className="text-sm text-foreground-800 hover:text-primary-600 transition-colors">
                          {siteConfig.brand.contact.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">客服电话</div>
                        <a href={`tel:${siteConfig.brand.contact.phone}`} className="text-sm text-foreground-800 hover:text-primary-600 transition-colors">
                          {siteConfig.brand.contact.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">微信</div>
                        <span className="text-sm text-foreground-800">{siteConfig.brand.contact.wechat}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">公司地址</div>
                        <span className="text-sm text-foreground-800">{siteConfig.brand.contact.address}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">工作时间</div>
                        <span className="text-sm text-foreground-800">{siteConfig.brand.contact.workingHours}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-5">
                    在线留言
                  </h3>
                  {formState === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-800 font-medium">{serverMessage}</p>
                      <button
                        onClick={() => setFormState('idle')}
                        className="mt-4 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                      >
                        发送新留言
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="true">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="hp-field"
                      />
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                            您的姓名 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                            电子邮箱 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                            留言主题
                          </label>
                          <input
                            id="contact-subject"
                            name="subject"
                            type="text"
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入留言主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                            留言内容 <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            rows={4}
                            required
                            maxLength={500}
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入您的留言内容（最多500字）"
                          ></textarea>
                          <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                        </div>
                        {formState === 'error' && (
                          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                            {formError}
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={formState === 'submitting'}
                          className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap"
                        >
                          {formState === 'submitting' ? (
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin"></i>提交中...
                            </span>
                          ) : (
                            '提交留言'
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}